import { Suspense, lazy, useMemo } from 'react';

import { CheckmarkCircle02Icon, Mail01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import * as animationData from 'assets/animations/success-tick.json';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button } from 'components/FormComponents';
import { ValidationAlert } from 'components/FormComponents/ValidationAlert/ValidationAlert';
import FadeInMotion from 'components/Motion/FadeInMotion';

const Lottie = lazy(() => import('react-lottie'));

export const EmailVerificationAlert = ({
  sent,
  resend,
  visble,
  onSignOut = () => {},
  user,
  error,
  interpreting = false,
}) => {
  const lottieOptions = useMemo(
    () => ({
      loop: false,
      autoplay: true,
      animationData: animationData,
    }),
    [],
  );

  return (
    <AnimatedModal visible={visble} noClose hideHeader xSmall>
      <div className="flex flex-col gap-8 p-4 items-center justify-center">
        <div className="flex flex-col gap-4 items-center justify-center">
          {sent ? (
            <Suspense fallback={<div>Loading...</div>}>
              <Lottie key="form-thanks-lottie" width={160} options={lottieOptions} />
            </Suspense>
          ) : (
            <div
              style={{ backgroundImage: `url('assets/images/verify_email.png')` }}
              className="h-[200px] w-full bg-cover bg-center rounded-3xl overflow-hidden"
            />
          )}
          <div className="flex flex-col gap-2 items-center justify-center">
            <h1>
              <span className="display-2 u-font-weight-600 brand-gradient-text">
                {sent ? 'Email' : 'Verify'}
              </span>
              <span className="display-2 u-font-weight-600">
                &nbsp;{sent ? 'sent' : 'your email'}
              </span>
            </h1>
            {sent ? (
              <span className="text-xl text-text-tertiary font-light text-center">
                To continue using Checkmate, please click the link in the verification email that we
                sent to your inbox.
              </span>
            ) : (
              <span className="text-xl text-text-tertiary font-light text-center">
                To continue using Checkmate, please verify your email by clicking the link in the
                confirmation email that we sent to your inbox.
              </span>
            )}
          </div>
        </div>
        {user && user?.email && (
          <div className="bg-neutral-2 border border-neutral-4 rounded-xl p-4 w-full">
            <div className="flex gap-2">
              <HugeiconsIcon
                icon={Mail01Icon}
                size={20}
                strokeWidth="2"
                className="text-primary-4"
              />
              <span className="text-xl text-neutral-6 font-regular">{user?.email}</span>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-4 items-center justify-center w-full">
          {error && (
            <FadeInMotion key="verification-error">
              <ValidationAlert show={true} message={error} />
            </FadeInMotion>
          )}
          <Button
            type={sent ? 'success' : 'primary'}
            onClick={sent ? () => {} : resend}
            className="w-full">
            {sent && (
              <HugeiconsIcon
                icon={CheckmarkCircle02Icon}
                size={20}
                strokeWidth="2"
                className="text-neutral-1 mr-4"
              />
            )}{' '}
            {sent ? 'Check your inbox' : 'Resend email verification'}
          </Button>
          <a className="u-link dark" onClick={onSignOut}>
            {interpreting ? 'Stop interpreting' : 'Sign out'}
          </a>
        </div>
        <div className="text-lg border-t border-neutral-4 pt-4 w-full text-center">
          <span className="text-text-tertiary font-regular">
            {sent
              ? "Can't find the email? check spam or contact us at"
              : 'For any other issues, contact us at'}
          </span>{' '}
          <a href="mailto:support@checkmate.tech" className="text-primary-4 font-medium">
            support@checkmate.tech
          </a>
        </div>
      </div>
    </AnimatedModal>
  );
};
